import React, {useCallback, useMemo} from "react";
import {memo} from "react";
import {NavigationProps} from "../../utils/navigationProps";
import {usePageTitle} from "../../hooks/usePageTitle";
import {MarkupEditor} from "./markup/markupEditor";
import {InvertedMarkupEditor} from "./markup/invertedMarkupEditor";
import {UnifiedPreview} from "./preview/unifiedPreview";
import {RootState} from "../../store/rootReducer";
import {useDispatch, useSelector} from "react-redux";
import {addCardActions} from "./addCard.slice";
import {markupFormatter} from "../../common";
import {styled} from "styled-components";
import {SideMenu} from "../../review/sideMenu/sideMenu";
import {uiActions} from "../../ui/ui.slice";
import {ThemeProps} from "../../styles/theme";
import {useTheme} from "../../styles/ThemeContext";

const selector = (state: RootState) => ({
    addInverted: state.addCard.addInverted,
    markup: state.addCard.markup,
    invertedMarkup: state.addCard.invertedMarkup,
    uploading: state.addCard.uploadInProgress,
});

const Root = styled.div`
    display: flex;
    flex-direction: column;
    height: 100%;
`;

const Content = styled.div<ThemeProps>`
    flex-grow: 1;
    display: flex;
    flex-direction: row;
    width: 100%;
    height: calc(100vh - 50px);
    padding: 0;
    font-size: 14px;
    color: ${props => props.theme.inputText};
    
    @media (max-width: 768px) {
        flex-direction: column;
        height: auto;
        min-height: calc(100vh - 50px);
    }
`;

const ActionBar = styled.div<ThemeProps>`
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    align-items: center;
    height: 50px;
    padding: 0 20px;
    background-color: ${props => props.theme.primaryBackground};
    border-bottom: 1px solid ${props => props.theme.primaryBorder};
`;

const Icon = styled.svg`
    width: 24px;
    height: 24px;
    fill: ${props => props.theme.inputText};
    cursor: pointer;
`;

const CardBlock = styled.div<ThemeProps>`
    background-color: ${props => props.theme.secondaryBackground};
    border: 1px solid ${props => props.theme.primaryBorder};
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    padding: 20px;
    margin-bottom: 20px;
    box-sizing: border-box;
    width: 100%;
`;

const InvertedCardBlock = styled(CardBlock)`
    margin-top: 20px;
`;

const LeftPane = styled.div<ThemeProps>`
    width: 50%;
    display: flex;
    flex-direction: column;
    overflow-y: auto;
    padding: 20px;
    box-sizing: border-box;
    border-right: 1px solid ${props => props.theme.primaryBorder};
    
    @media (max-width: 768px) {
        width: 100%;
        border-right: none;
        border-bottom: 1px solid ${props => props.theme.primaryBorder};
        max-height: 50vh;
        overflow-y: auto;
    }
`;

const RightPane = styled.div`
    width: 50%;
    display: flex;
    flex-direction: column;
    
    @media (max-width: 768px) {
        width: 100%;
        height: 50vh;
        min-height: 400px;
    }
`;

const EditorContainer = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    margin-bottom: 20px;
`;

const EditorTitle = styled.div<ThemeProps>`
    font-size: 16px;
    font-weight: 500;
    margin-bottom: 10px;
    color: ${props => props.theme.primaryText};
`;


const BlockTitle = styled.h2`
    font-size: 18px;
    font-weight: 500;
    margin-bottom: 15px;
    color: ${props => props.theme.primaryText};
`;

const AddInvertedGroup = styled.div<ThemeProps>`
    background-color: ${props => props.theme.secondaryBackground};
    border: 1px solid ${props => props.theme.primaryBorder};
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    padding: 20px;
    margin-bottom: 20px;
    box-sizing: border-box;
    width: 100%;
    color: ${props => props.theme.inputText};
    display: flex;
    align-items: center;
    
    input {
        margin-right: 10px;
    }
    
    label {
        cursor: pointer;
        user-select: none;
        font-size: 16px;
        font-weight: 500;
    }
`;

const SubmitButtonsGroup = styled.div<ThemeProps>`
    background-color: ${props => props.theme.secondaryBackground};
    border: 1px solid ${props => props.theme.primaryBorder};
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    padding: 20px;
    margin-top: auto;
    box-sizing: border-box;
    width: 100%;
    display: flex;
    flex-direction: row;
    justify-content: flex-end;
    
    button {
        padding: 10px 20px;
        font-size: 14px;
        font-weight: 500;
        border: none;
        border-radius: 4px;
        background-color: ${props => props.theme.primaryColor};
        color: white;
        cursor: pointer;
        
        &:hover {
            background-color: ${props => props.theme.accentColorDark};
        }
        
        &:disabled {
            background-color: ${props => props.theme.neutralButtonBackground};
            color: ${props => props.theme.secondaryText};
            cursor: not-allowed;
        }
    }
`;

const Fadeout = styled.div<ThemeProps>`
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: ${props => props.theme.cardShadow};
    opacity: 0.5;
    z-index: 1000;
`;


// Wrapper components to fix TypeScript errors
const MarkupEditorWrapper = () => <MarkupEditor />;
const InvertedMarkupEditorWrapper = () => <InvertedMarkupEditor />;

const AddCardComponent: React.FC<NavigationProps> = memo((_: NavigationProps) => {
    usePageTitle('New Card · Memkeep');
    const {markup, invertedMarkup, addInverted, uploading} = useSelector(selector);
    const dispatch = useDispatch();
    const { theme } = useTheme();

    const addInvertedChangedHandler = useCallback(() => {
        dispatch(addCardActions.toggleAddInverted());
    }, [dispatch]);

    const error = useMemo(() => {
        const cardTree = markupFormatter.parse(markup);
        return cardTree.error as string;
    }, [markup]);

    const invertedError = useMemo(() => {
        if (!addInverted) return '';
        const cardTree = markupFormatter.parse(invertedMarkup);
        return cardTree.error as string;
    }, [addInverted, invertedMarkup]);


    const addClickHandler = useCallback(() => {
        dispatch(addCardActions.uploadCard());
    }, [dispatch]);
    
    const openMenuHandler = useCallback(() => {
        dispatch(uiActions.openSideMenu());
    }, [dispatch]);

    return (
        <Root>
            <ActionBar theme={theme}>
                <Icon xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" onClick={openMenuHandler}>
                    <path d="M0 0h24v24H0z" fill="none"/>
                    <path d="M3 18h18v-2H3v2zm0-5h18v-2H3v2zm0-7v2h18V6H3z"/>
                </Icon>
            </ActionBar>
            <Content theme={theme}>
                <LeftPane theme={theme}>
                    <AddInvertedGroup theme={theme}>
                        <input id="add-inverted" type="checkbox" checked={addInverted}
                               onChange={addInvertedChangedHandler}/>
                        <label htmlFor="add-inverted">Add inverted Card as well</label>
                    </AddInvertedGroup>

                    {/* Original Card Block */}
                    <CardBlock theme={theme}>
                        <BlockTitle>Card</BlockTitle>
                        <EditorContainer>
                            <EditorTitle theme={theme}>Markup</EditorTitle>
                            <MarkupEditorWrapper/>
                        </EditorContainer>
                    </CardBlock>

                    {/* Inverted Card Block (shown only when checkbox is checked) */}
                    {addInverted && (
                        <InvertedCardBlock theme={theme}>
                            <BlockTitle>Inverted Card</BlockTitle>
                            <EditorContainer>
                                <EditorTitle theme={theme}>Markup</EditorTitle>
                                <InvertedMarkupEditorWrapper/>
                            </EditorContainer>
                        </InvertedCardBlock>
                    )}

                    <SubmitButtonsGroup theme={theme}>
                        <button
                            data-testid="card-save"
                            disabled={error !== '' || (addInverted && invertedError !== '')}
                            onClick={addClickHandler}
                            title={error || invertedError}
                        >
                            Add {addInverted ? 'Cards' : 'Card'}
                        </button>
                    </SubmitButtonsGroup>
                </LeftPane>
                
                <RightPane>
                    <UnifiedPreview />
                </RightPane>
            </Content>
            {uploading && (
                <Fadeout theme={theme} />
            )}
            <SideMenu />
        </Root>
    );
});

AddCardComponent.displayName = 'AddCardComponent';

export { AddCardComponent as AddCard };
